/**
 * Save a generated file so it actually lands in the user's Downloads
 * / Files instead of opening as an inline browser preview.
 *
 * Princewill's report (2026-11-04, iPhone Safari): "The PDF is not
 * downloading - it just opens in the browser without saving the PDF
 * in Downloads as it's supposed to." Two delivery bugs caused it:
 *
 *  1. jsPDF's doc.save() hands the blob to FileSaver, which clicks a
 *     temporary anchor inside a setTimeout - after the app's async
 *     capture work the click no longer carries the user's gesture,
 *     and iOS Safari treats it as a navigation: the PDF opens as a
 *     preview and is never saved.
 *  2. The server-PDF handler created a blob URL, clicked an anchor
 *     and revoked the URL synchronously in the same tick, which can
 *     abort the hand-off on iOS before Safari has read the blob.
 *
 * This helper is the one delivery path for every generated PDF:
 *  - Everywhere except iOS: an anchor with the `download` attribute
 *    and a blob URL, revoked on a delay - a plain file download.
 *  - iOS Safari: blob downloads of PDFs are navigations there, so
 *    the reliable save route is the Web Share sheet carrying the
 *    file ("Save to Files"). It is used only on iOS and only when
 *    the device can share files; if sharing is unavailable the
 *    anchor download remains as the fallback, and if the user
 *    cancels the share sheet nothing else is triggered.
 */

export type FileSaveOutcome = 'download' | 'shared' | 'cancelled';

export const isIOSDevice = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iP(hone|ad|od)/.test(ua)) return true;
    // iPadOS identifies as macOS Safari but has a touch screen.
    return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
};

export async function saveBlobAsDownload(blob: Blob, filename: string): Promise<FileSaveOutcome> {
    if (isIOSDevice() && typeof navigator !== 'undefined' && 'canShare' in navigator && 'share' in navigator) {
        try {
            const file = new File([blob], filename, { type: blob.type || 'application/pdf' });
            if ((navigator as any).canShare({ files: [file] })) {
                await (navigator as any).share({ files: [file] });
                return 'shared';
            }
        } catch (err: any) {
            // The user closing the share sheet is a choice, not an
            // error - and it must not fall through to a navigation.
            if (err && err.name === 'AbortError') return 'cancelled';
            // Any other share failure falls through to the download.
        }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke LATE: revoking in the same tick as click() can abort the
    // download before the browser has read the blob (iOS Safari).
    window.setTimeout(() => URL.revokeObjectURL(url), 4000);
    return 'download';
}
